import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Allocations } from 'components/AllocationsTable/types';
import { capitalAction as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useDefaultEntryValues from './useDefaultEntryValues';
import { CommitmentType } from 'further-types/lp/vehicle';
import { ActionStatus } from 'further-types/lp/action';
import { useLayoutContext } from 'contexts/LayoutContext';

const formSchema = (id?: string) =>
  id ? rules.UpdateCapitalAction : rules.CreateCapitalAction;

export enum Steps {
  Details,
  Transactions,
  Review,
}

type CapitalAction = {
  id: string;
  status: ActionStatus;
};

export type Action = 'saveAndExit' | 'saveAndNext';

export type CapitalActionFormState = {
  allocations: Record<string, Array<Allocations>>;
};

export type CreateCapitalActionFormValues = z.infer<
  typeof rules.CreateCapitalAction
>;
export type UpdateCapitalActionFormValues = z.infer<
  typeof rules.UpdateCapitalAction
>;
type CapitalActionFormValues =
  | CreateCapitalActionFormValues
  | UpdateCapitalActionFormValues;

function getStepValidationFields(step: Steps) {
  switch (step) {
    case Steps.Details:
      return ['vehicleId', 'name', 'ledgerDate', 'actionDueDate'] as const;
    case Steps.Transactions:
      return ['entries'] as const;
  }

  return [];
}

function isUpdateValues(
  values: CapitalActionFormValues,
): values is UpdateCapitalActionFormValues {
  return 'id' in values;
}

export default function useCapitalActionForm({
  id,
  vehicleId,
  defaultValues,
  hasUncalledEqualisations,
}: {
  id?: string;
  vehicleId: string;
  defaultValues?: UpdateCapitalActionFormValues;
  hasUncalledEqualisations: boolean;
}) {
  const apiUtils = api.useUtils();
  const [lastAction, setLastAction] = useState<Action | null>(null);
  const { vehicle } = useLayoutContext();
  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;
  const hasUncalledCapitalContribution =
    vehicle?.hasUncalledCapitalContribution ?? false;
  const { defaultEntry } = useDefaultEntryValues({
    capitalContributionIsDefault:
      isCapitalAndLoan && hasUncalledCapitalContribution,
    equalisationIsDefault: hasUncalledEqualisations,
  });

  const formMethods = useForm<CapitalActionFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema(id)),
    defaultValues: {
      ...(id ? { id } : {}),
      vehicleId,
      name: '',
      status: ActionStatus.Draft,
      ledgerDate: undefined,
      actionDueDate: undefined,
      entries: [defaultEntry],
      transactions: [],
    },
    values: defaultValues
      ? {
          ...defaultValues,
          vehicleId,
          entries: defaultValues?.entries?.length
            ? defaultValues?.entries
            : [defaultEntry],
        }
      : undefined,
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  const createCapitalAction = api.capitalAction.create.useMutation();
  const updateCapitalAction = api.capitalAction.update.useMutation();

  const getUpdateValues = (
    values: UpdateCapitalActionFormValues,
    step: Steps,
  ) => {
    switch (step) {
      case Steps.Details:
        return {
          id: values.id,
          vehicleId: values.vehicleId,
          name: values.name,
          ledgerDate: values.ledgerDate,
          actionDueDate: values.actionDueDate,
          status: values.status,
        };
      default:
        return values;
    }
  };

  const handleSubmit = (
    values: CapitalActionFormValues,
    step: Steps,
    onSuccess: (capitalAction: CapitalAction) => void,
  ) => {
    const mutateOptions = {
      onSuccess: async (capitalAction: CapitalAction) => {
        onSuccess(capitalAction);
        await Promise.all([
          apiUtils.capitalAction.get.invalidate(),
          apiUtils.capitalAction.getVehicleCapitalActionsSummary.invalidate(),
        ]);
      },
    };

    if (isUpdateValues(values)) {
      const updateValues = getUpdateValues(values, step);
      updateCapitalAction.mutate(updateValues, mutateOptions);
    } else {
      createCapitalAction.mutate(values, mutateOptions);
    }
  };

  const submitStep = async (
    step: Steps,
    onSuccess: (capitalAction: CapitalAction) => void,
    options?: { approve?: boolean },
  ) => {
    const shouldApprove = options?.approve ?? step === Steps.Review;
    if (shouldApprove) {
      formMethods.handleSubmit((values) =>
        handleSubmit(
          { ...values, status: ActionStatus.Approved },
          Steps.Review,
          onSuccess,
        ),
      )();
    } else if (await formMethods.trigger(getStepValidationFields(step))) {
      handleSubmit(formMethods.getValues(), step, onSuccess);
    }
  };

  return {
    formMethods,
    submitStep,
    isLoading: createCapitalAction.isPending || updateCapitalAction.isPending,
    lastAction,
    setLastAction,
  };
}
